import { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, ChevronDown, Check, X, RotateCcw, ArrowDown, Linkedin, UserRound } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { validCvFile } from "../app/CvUploader";
import LinkedInGuide from "../app/LinkedInGuide";
import { GUEST_KEY } from "../studio/constants";
import CreateProfile from "./CreateProfile";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const STAGES = ["Reading your CV", "Checking ATS software can read it", "Looking at your achievements and wording", "Writing your top fixes"];
const LI_STAGES = ["Reading your LinkedIn profile", "Writing your CV", "Checking ATS software can read it", "Writing your top fixes"];
const validLinkedIn = (f) => (!f ? "Choose your LinkedIn PDF first." : !f.name.toLowerCase().endsWith(".pdf") ? "Please choose the PDF you saved from LinkedIn." : f.size > 8 * 1024 * 1024 ? "That file is over 8MB." : "");

export function useFreeCheck() {
  const [state, setState] = useState(() => (localStorage.getItem(GUEST_KEY) ? "processing" : "idle"));
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("cv");
  const run = useRef(0);

  const poll = useCallback(async (token) => {
    const my = ++run.current;
    setState("processing");
    const started = Date.now();
    while (run.current === my && Date.now() - started < 240000) {
      try {
        const { data } = await api.get(`/guest/cv-check/${token}`);
        if (run.current !== my) return;
        if (data.source) setMode((m) => (m === "profile" ? m : data.source));
        if (data.status === "done") { setResult(data); setState("done"); return; }
        if (data.status === "failed") { localStorage.removeItem(GUEST_KEY); setState("failed"); return; }
      } catch (e) {
        if (e?.response?.status === 404) { localStorage.removeItem(GUEST_KEY); setState("idle"); return; }
      }
      await sleep(2500);
    }
    if (run.current === my) { localStorage.removeItem(GUEST_KEY); setState("failed"); }
  }, []);

  useEffect(() => {
    const t = localStorage.getItem(GUEST_KEY);
    if (t) poll(t);
    return () => { run.current++; }; // eslint-disable-line react-hooks/exhaustive-deps
  }, [poll]);

  const start = async ({ file, jd }) => {
    setState("uploading");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("jd_text", jd || "");
      form.append("consent", "true");
      form.append("source", mode);
      const { data } = await api.post("/guest/cv-check", form, { headers: { "Content-Type": "multipart/form-data" } });
      localStorage.setItem(GUEST_KEY, data.token);
      poll(data.token);
    } catch (e) {
      setState("idle");
      toast.error(e?.response?.data?.detail || "We couldn't check that file. Please try again.");
    }
  };
  const reset = () => { run.current++; localStorage.removeItem(GUEST_KEY); setResult(null); setState("idle"); };
  const remove = async () => {
    const t = localStorage.getItem(GUEST_KEY);
    try {
      if (t) await api.delete(`/guest/cv-check/${t}`);
      toast.success("Deleted. We no longer hold your CV.");
    } catch {
      toast.error("Couldn't delete it just now. Please try again in a moment.");
      return;
    }
    reset();
  };
  return { state, result, start, reset, remove, mode, setMode };
}

function Progress({ uploading, linkedin }) {
  const stages = linkedin ? LI_STAGES : STAGES;
  const [secs, setSecs] = useState(0);
  useEffect(() => { const t = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(t); }, []);
  const i = uploading ? 0 : Math.min(stages.length - 1, Math.floor(secs / (linkedin ? 13 : 10)));
  return (
    <div className="mt-5" data-testid="free-check-progress" aria-live="polite">
      <ol className="space-y-2.5">
        {stages.map((s, n) => (
          <li key={s} className={`flex items-center gap-3 text-[15px] ${n <= i ? "text-ink" : "text-faint"}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${n < i ? "bg-brand-soft text-brand-text" : n === i ? "bg-brand text-onbrand" : "bg-subtle"}`}>
              {n < i ? <Check className="w-3.5 h-3.5" /> : n === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            </span>
            {s}
          </li>
        ))}
      </ol>
      <p className="mt-4 text-sm text-faint">Usually takes 30 to 60 seconds. You can stay on this page.</p>
    </div>
  );
}

function Picker({ file, onPick, linkedin }) {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  return (
    <div data-testid="free-check-dropzone" role="button" tabIndex={0} aria-label="Choose your CV file"
      onClick={() => ref.current?.click()}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onPick(e.dataTransfer.files?.[0]); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-5 sm:p-6 flex items-center gap-4 transition-colors ${over ? "border-brand bg-brand-soft" : file ? "border-brand/50 bg-brand-soft/40" : "border-line-strong/50 hover:border-brand/60 hover:bg-brand-soft/40"}`}>
      <input ref={ref} data-testid="free-check-file-input" type="file" accept={linkedin ? ".pdf,application/pdf" : ".pdf,.docx,.txt"} className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
      <span className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
        {file ? <FileText className="w-5 h-5 text-brand" /> : linkedin ? <Linkedin className="w-5 h-5 text-brand" /> : <UploadCloud className="w-5 h-5 text-brand" />}
      </span>
      <span className="min-w-0 text-left">
        <span className={`block font-semibold text-ink ${file ? "truncate" : "break-words"}`} data-testid="free-check-file-name">
          {file ? file.name : <><span className="sm:hidden">Tap to choose your {linkedin ? "LinkedIn PDF" : "CV"}</span><span className="hidden sm:inline">Drop your {linkedin ? "LinkedIn PDF" : "CV"} here or click to choose</span></>}
        </span>
        <span className="block text-sm text-faint">{file ? "Tap to choose a different file" : linkedin ? "The PDF from LinkedIn's Save to PDF" : "PDF, Word or text · up to 8MB"}</span>
      </span>
    </div>
  );
}

function Form({ onStart, linkedin }) {
  const [file, setFile] = useState(null);
  const [withJob, setWithJob] = useState(false);
  const [jd, setJd] = useState("");
  const [consent, setConsent] = useState(false);
  const pick = (f) => { const err = linkedin ? validLinkedIn(f) : validCvFile(f); if (err) return toast.error(err); setFile(f); };
  const submit = () => {
    if (!file) return toast.error(linkedin ? "Choose your LinkedIn PDF first." : "Choose your CV file first.");
    if (!consent) return toast.error("Please tick the box to agree first.");
    const job = withJob ? jd.trim() : "";
    if (job && job.length < 40) return toast.error("Paste the full job ad, or leave it empty for a CV-only check.");
    onStart({ file, jd: job });
  };
  return (
    <div className="mt-5 space-y-4">
      {linkedin && <LinkedInGuide compact />}
      <Picker file={file} onPick={pick} linkedin={linkedin} />
      <div>
        <button type="button" data-testid="free-check-job-toggle" onClick={() => setWithJob((v) => !v)} aria-expanded={withJob}
          className="flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-hover">
          <ChevronDown className={`w-4 h-4 transition-transform ${withJob ? "rotate-180" : ""}`} /> Add a job ad to see your match <span className="text-faint font-normal">(optional)</span>
        </button>
        {withJob && (
          <textarea data-testid="free-check-jd-input" value={jd} onChange={(e) => setJd(e.target.value)} rows={4} maxLength={12000} aria-label="Job ad"
            placeholder="Paste the job ad here" className="gi-input mt-2 resize-y leading-relaxed" />
        )}
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="checkbox" data-testid="free-check-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-brand shrink-0" />
        <span className="text-sm text-body">I agree to the <a href="/privacy" target="_blank" rel="noreferrer" className="text-brand hover:underline">Privacy Policy</a>. My CV is kept until I delete it.</span>
      </label>
      <button data-testid="free-check-submit" onClick={submit} className="w-full gi-btn gi-btn-primary gi-btn-lg">{linkedin ? "Build my CV free" : "Check my CV free"}</button>
    </div>
  );
}

const MODES = [["cv", "I have a CV", "My CV", FileText], ["linkedin", "Use my LinkedIn", "LinkedIn", Linkedin], ["profile", "Create my profile", "New profile", UserRound]];
const COPY = {
  cv: ["Free CV check", "No sign-up needed", "How strong is your CV?", "An honest score and your top fixes in about a minute."],
  linkedin: ["Free CV builder", "No sign-up needed", "Turn your LinkedIn into a CV", "A polished CV from your profile, plus your top fixes, in about a minute."],
  profile: ["Free profile", "Verified by email", "Create your profile", "Confirm your email, then add your details and build a polished CV from scratch."],
};

function ModeToggle({ mode, onChange }) {
  return (
    <div role="tablist" aria-label="What do you want to do?" className="mt-4 grid grid-cols-3 gap-1 p-1 rounded-xl bg-subtle">
      {MODES.map(([id, label, short, Icon]) => (
        <button key={id} role="tab" aria-selected={mode === id} data-testid={`free-check-mode-${id}`} onClick={() => onChange(id)}
          className={`flex items-center justify-center gap-2 px-2 sm:px-3 py-2 min-h-[42px] rounded-lg text-[13px] sm:text-sm font-semibold transition-colors ${mode === id ? "bg-surface text-ink shadow-card" : "text-body hover:text-ink"}`}>
          <Icon className="w-4 h-4 hidden sm:block lg:hidden xl:block shrink-0" aria-hidden="true" /> <span className="sm:hidden lg:inline xl:hidden whitespace-nowrap">{short}</span><span className="hidden sm:inline lg:hidden xl:inline whitespace-nowrap">{label}</span>
        </button>
      ))}
    </div>
  );
}

export function FreeCheckCard({ fc }) {
  const busy = fc.state === "uploading" || fc.state === "processing";
  const li = fc.mode === "linkedin";
  const pr = fc.mode === "profile";
  const idle = !busy && !(fc.state === "done" && fc.result);
  const [eyebrow, note, title, sub] = COPY[fc.mode] || COPY.cv;
  return (
    <div id="free-check" data-testid="free-check-card" className="gi-card shadow-float p-5 sm:p-7 scroll-mt-24">
      <div className="flex items-center justify-between gap-3">
        <span className="gi-eyebrow">{eyebrow}</span>
        <span className="text-xs font-medium text-faint" data-testid="free-check-note">{note}</span>
      </div>
      <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mt-2" data-testid="free-check-title">{title}</h2>
      <p className="text-[15px] text-body mt-1">{sub}</p>
      {(idle || pr) && <ModeToggle mode={fc.mode} onChange={fc.setMode} />}
      {pr ? <CreateProfile /> : busy ? <Progress uploading={fc.state === "uploading"} linkedin={li} /> : fc.state === "done" && fc.result ? (
        <div className="mt-5 rounded-xl bg-brand-soft/60 border border-brand/20 p-4" data-testid="free-check-ready">
          <div className="font-semibold text-ink">{fc.result.generated_cv ? "Your new CV is ready · CV health " : "Your CV scored "}{fc.result.health?.overall ?? 0}%{fc.result.match ? ` · ${fc.result.match.overall}% job match` : ""}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="#free-check-result" data-testid="free-check-see-results" className="gi-btn gi-btn-primary gi-btn-sm"><ArrowDown className="w-4 h-4" /> {fc.result.generated_cv ? "See and download it" : "See my results"}</a>
            <button data-testid="free-check-again-top" onClick={fc.reset} className="gi-btn gi-btn-secondary gi-btn-sm"><RotateCcw className="w-4 h-4" /> Start again</button>
          </div>
        </div>
      ) : (
        <>
          {fc.state === "failed" && (
            <div role="alert" data-testid="free-check-failed" className="mt-5 rounded-xl bg-danger-soft border border-danger/20 p-3.5 text-sm text-ink flex gap-2">
              <X className="w-4 h-4 text-danger mt-0.5 shrink-0" /> That didn't finish this time. Please try again.
            </div>
          )}
          <Form key={fc.mode} onStart={fc.start} linkedin={li} />
        </>
      )}
    </div>
  );
}
